import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IFilaNSDevice, IResumenFilasNS } from 'modelos/src';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { InfoBannerComponent } from '../../../../../componentes/info-banner/info-banner.component';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { ChipDatoComponent } from './chip-dato.component';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DispositivosLorawanService } from '../dispositivos-lorawan.service';
import { FilaNSDialogComponent } from './fila-ns-dialog.component';
import { fechaCelda } from '../../../../../auxiliares/tabla/celdas';

// PANEL GENERAL DE FILAS DEL NS (nivel 0).
//
// La vista por dispositivo contesta "¿qué tiene encolado ESTE nodo?". Ésta
// contesta la que importa cuando el scheduler de Chirpstack está en bucle:
// "¿QUIÉNES tienen fila sucia?". La tasa de error del scheduler mide nodos, no
// items, así que para cortar el bucle hay que vaciar TODOS los sucios: con un
// solo nodo pendiente el error sigue apareciendo en cada tick.
//
// Escanea sólo clase B/C: un clase A no puede acumular fila huérfana, porque su
// downlink sale en la ventana RX de un uplink y su scheduler no agenda sin él.
@Component({
  selector: 'app-filas-ns-panel',
  standalone: true,
  imports: [TooltipDirective, 
    CommonModule,
    FormsModule,
    BOTONES,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CardHeaderComponent,
    InfoBannerComponent,
    ChipDatoComponent,
  ],
  templateUrl: './filas-ns-panel.component.html',
  styleUrl: './filas-ns-panel.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FilasNSPanelComponent implements OnInit {
  public dialogRef = inject<MatDialogRef<FilasNSPanelComponent>>(MatDialogRef);
  private formularios = inject(FormularioDialogService);
  private service = inject(DispositivosLorawanService);
  private helper = inject(HelperService);
  private dialog = inject(DialogService);

  public resumen?: IResumenFilasNS;
  public cargando = false;
  public trabajando = false;

  // Por defecto se mira TODO el parque clase B/C. El acotado a offline es el
  // mismo alcance que el barrido automático —donde vive el problema—, útil para
  // comparar contra lo que el barrido va a tocar en su próxima pasada.
  public soloOffline = false;

  // Filas sin nada que perder: todos sus items vencieron o no tienen
  // vencimiento. Los segundos son los que el network server nunca descarta
  // solo, así que tratarlos como "vigentes" dejaría al device sucio para
  // siempre — justo el que sostiene el bucle del scheduler.
  get descartables(): IFilaNSDevice[] {
    return (this.resumen?.devices ?? []).filter((d) => d.todosDescartables);
  }

  public cerrar(): void {
    this.dialogRef.close();
  }

  async ngOnInit(): Promise<void> {
    await this.cargar();
  }

  public async cargar(): Promise<void> {
    if (this.cargando) return;
    this.cargando = true;
    try {
      this.resumen = await this.service.getResumenFilasNS(this.soloOffline);
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.cargando = false;
    }
  }

  public verDetalle(device: IFilaNSDevice): void {
    this.formularios.abrir(FilaNSDialogComponent, {
      tamano: 'mediano',
      cerrarAfuera: true,
      data: {
        idDispositivo: device.idDispositivo,
        nombre: device.name || device.deveui,
      },
    });
  }

  public async flushUno(device: IFilaNSDevice): Promise<void> {
    const vigentes =
      device.totalItems - device.itemsVencidos - device.itemsSinVencimiento;
    const advertencia = vigentes
      ? `\n\nOJO: ${vigentes} de esos ${device.totalItems} siguen VIGENTES.`
      : '';
    const ok = await this.dialog.confirm(
      'Vaciar la fila del network server',
      `Se borran los ${device.totalItems} downlinks pendientes de ` +
        `${device.name || device.deveui}.${advertencia}`,
      'Vaciar',
      'Cancelar',
    );
    if (!ok) return;
    await this.flushear([device.deveui]);
  }

  // Vaciar sólo las filas cuyos items TODOS vencieron: por definición de la
  // plataforma eso ya es basura (todo downlink sale con `expiresAt`), así que
  // no puede perderse nada útil. Es la misma regla del barrido automático.
  public async flushDescartables(): Promise<void> {
    const objetivo = this.descartables;
    if (!objetivo.length) return;
    const items = objetivo.reduce((n, d) => n + d.totalItems, 0);
    const ok = await this.dialog.confirm(
      'Vaciar las filas sin valor',
      `${objetivo.length} dispositivo(s) no tienen NINGÚN downlink que valga ` +
        `la pena entregar (${items} items en total): o vencieron, o no tienen ` +
        `vencimiento y el network server no los va a descartar nunca por su ` +
        `cuenta.` +
        `\n\nLos que todavía tienen algún downlink VIGENTE no se tocan.`,
      'Vaciar',
      'Cancelar',
    );
    if (!ok) return;
    await this.flushear(objetivo.map((d) => d.deveui));
  }

  private async flushear(deveuis: string[]): Promise<void> {
    this.trabajando = true;
    try {
      const r = await this.service.flushFilasNSMasivo(deveuis);
      if (r.errores) {
        this.helper.notifWarn(
          `Vaciadas ${r.flusheados} de ${r.pedidos} filas ` +
            `(${r.itemsBorrados} downlinks). ${r.errores} con error.`,
        );
      } else {
        this.helper.notifSuccess(
          `Vaciadas ${r.flusheados} fila(s): ${r.itemsBorrados} downlinks borrados`,
        );
      }
      await this.cargar();
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.trabajando = false;
    }
  }

  public formatearFecha(fecha?: string): string {
    return fecha ? fechaCelda(fecha) : 'nunca';
  }
}
